/**
 * Le due cornici di sistema: la barra di stato e la tastiera.
 *
 * ## Barra di stato
 *
 * il guscio la dipinge di un azzurro che non è di nessuno. Ma il branding
 * qui è **per tenant**: ogni cliente ha il proprio colore primario, applicato alle CSS variable di
 * Mantine da `uiTheme.applyBrandColors`. Il risultato, dentro l'app, è che ogni cliente apre la
 * propria applicazione e trova in cima la striscia di un altro.
 *
 * La configurazione statica non può saperlo — il colore arriva da una query, dopo l'avvio — quindi
 * la barra va ridipinta nello stesso momento in cui si dipingono i pulsanti.
 *
 * Il colore del **testo** non si sceglie: si calcola. Su un bordeaux scuro servono icone chiare, su
 * un giallo servono scure, e sbagliare rende l'ora e la batteria illeggibili. La luminanza
 * percepita lo decide meglio di qualsiasi elenco di casi.
 *
 * ## Tastiera
 *
 * Nella WebView la tastiera copre la pagina invece di restringerla: i bottom-sheet con dei campi —
 * registrare un danno, compilare il report di una trasferta — finiscono sotto, insieme al pulsante
 * che li salva. Chi scrive non vede cosa scrive.
 *
 * `resize: 'native'` fa restringere la WebView al sistema, che è la modalità che si comporta come
 * un'app vera. In più l'altezza della tastiera viene esposta come variabile CSS
 * (`--altezza-tastiera`), così un pannello può lasciarle spazio senza indovinare un numero.
 */
import { nativeRuntime } from '@/shared/lib/nativePlatform';

/* ─────────────────────────── barra di stato ─────────────────────────── */

const HEX = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;

function expand(hex: string): string {
  const h = hex.replace('#', '');
  return h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
}

/**
 * `true` se su questo colore il testo va scritto **chiaro**.
 *
 * Formula di luminanza relativa (WCAG): i tre canali non pesano uguale perché l'occhio non li vede
 * uguali — il verde conta più del doppio del rosso, il blu quasi niente. Una media semplice
 * sbaglierebbe proprio sui blu scuri e sui gialli, che sono i due colori di brand più frequenti.
 */
export function lightTextOn(color: string): boolean {
  if (!HEX.test(color)) return true;
  const h = expand(color);
  const channel = (i: number) => {
    const v = parseInt(h.slice(i * 2, i * 2 + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
  return luminance < 0.45;
}

/**
 * Dipinge la barra di stato col colore del tenant. Un colore non valido non viene applicato: meglio
 * la barra di prima che una barra trasparente su icone bianche.
 *
 * Su iOS `setBackgroundColor` non ha effetto (la barra è disegnata dal sistema sopra il contenuto),
 * ma lo stile chiaro/scuro sì, ed è quello che conta per la leggibilità.
 */
export function applyStatusBarColor(primaryColor?: string | null): void {
  if (nativeRuntime() === 'web' || !primaryColor || !HEX.test(primaryColor)) return;
  const hex = `#${expand(primaryColor)}`;

  paintStatusBarCordova(hex);
}

/* ─────────────────────────── tastiera ─────────────────────────── */

/** La variabile CSS con l'altezza della tastiera. `0px` quando è chiusa. */
export const VAR_KEYBOARD_HEIGHT = '--altezza-tastiera';

/**
 * Configura la tastiera e pubblica la sua altezza come variabile CSS.
 *
 * ```css
 * .foglio { padding-bottom: calc(20px + var(--altezza-tastiera, 0px)); }
 * ```
 */
export function initKeyboard(): void {
  if (nativeRuntime() !== 'cordova') return;
  hookCordovaKeyboard();
}

/* ───────────────────────────── il ramo Cordova ───────────────────────────── */

/**
 * `cordova-plugin-statusbar`, con i metodi letti dal suo
 * `www/statusbar.js`: `backgroundColorByHexString`, `styleDefault`,
 * `styleLightContent`.
 *
 * ⚠️ I nomi dei due stili dicono **il contenuto**, non lo sfondo: `styleLightContent` va su un
 * fondo **scuro**. E' l'errore classico con questo plugin, e lo era in doppio finche' c'era
 * anche il plugin del guscio precedente, dove `Style.Dark` descriveva invece **lo sfondo** — la stessa
 * cosa scritta al rovescio, in due file che si leggevano di fianco.
 */
interface StatusBarCordova {
  backgroundColorByHexString(hex: string): void;
  styleDefault(): void;
  styleLightContent(): void;
}

function paintStatusBarCordova(hex: string): void {
  if (typeof window === 'undefined') return;
  const sb = (window as { StatusBar?: StatusBarCordova }).StatusBar;
  if (!sb) return;
  try {
    sb.backgroundColorByHexString(hex);
    if (lightTextOn(hex)) sb.styleLightContent();
    else sb.styleDefault();
  } catch { /* la barra resta com'era: e' cosmetica, non vale un errore in faccia */ }
}

/**
 * L'altezza della tastiera senza plugin, con `visualViewport`.
 *
 * ⚠️ Il `LEGGIMI` del guscio dice che la tastiera **non ha un corrispondente
 * Cordova** e che la strada e' l'API del browser: e' quella. `visualViewport` racconta la
 * finestra **visibile**, e quando la tastiera si apre quella si accorcia — la differenza
 * dall'altezza totale e' l'altezza della tastiera.
 *
 * ⚠️ Non e' equivalente al plugin precedente su un punto: quello mandava `keyboardWillShow`, cioe'
 * **prima** dell'animazione; qui l'evento arriva mentre la finestra si ridimensiona. Il valore
 * finale e' lo stesso, l'aggiornamento e' un po' piu' tardi. Per un padding va bene; per
 * un'animazione sincronizzata col sistema no, e va saputo prima di provarci.
 */
function hookCordovaKeyboard(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const vv = window.visualViewport;

  const write = (px: number) => {
    document.documentElement.style.setProperty(VAR_KEYBOARD_HEIGHT, `${Math.max(0, Math.round(px))}px`);
  };
  write(0);
  if (!vv) return;

  const update = () => {
    /* `offsetTop` conta quando la pagina e' stata spinta in su dal sistema: senza, su iOS la
       tastiera risulterebbe piu' bassa di quanto e'. */
    write(window.innerHeight - vv.height - vv.offsetTop);
  };
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  update();
}
